"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Popover as PopoverPrimitive } from "radix-ui";
import { ChevronDown } from "@/components/icons";
import { cn } from "@/lib/utils";
import { NavMenuLinks } from "./nav-links";
import { useNavActive } from "./use-nav-active";
import { NAV_MENUS, RESOURCES_MENU } from "./nav-data";
import type { NavMenu } from "./types";

const VIEWPORT_PADDING = 16;

function useViewportClamp(open: boolean) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [horizontalOffset, setHorizontalOffset] = useState(0);

  useEffect(() => {
    if (!open) return;
    const panelEl = panelRef.current;
    if (!panelEl) return;

    const updatePosition = () => {
      const { left, right } = panelEl.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      let nextOffset = 0;

      if (left < VIEWPORT_PADDING) {
        nextOffset = VIEWPORT_PADDING - left;
      } else if (right > viewportWidth - VIEWPORT_PADDING) {
        nextOffset = viewportWidth - VIEWPORT_PADDING - right;
      }

      setHorizontalOffset((current) =>
        Math.abs(current - nextOffset) < 0.5 ? current : nextOffset,
      );
    };

    const frame = requestAnimationFrame(updatePosition);
    const handleResize = () => requestAnimationFrame(updatePosition);
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", handleResize);
    };
  }, [open]);

  return { panelRef, offset: open ? horizontalOffset : 0 };
}

function MenuPanel({
  open,
  widthClassName,
  children,
}: {
  open: boolean;
  widthClassName: string;
  children: ReactNode;
}) {
  const { panelRef, offset } = useViewportClamp(open);

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={panelRef}
        side="bottom"
        align="center"
        sideOffset={8}
        className={`z-50 ${widthClassName} rounded-2xl border border-border bg-card p-3 shadow-xl shadow-brand/5 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 data-[state=open]:duration-150 data-[state=closed]:duration-100`}
        style={{ marginLeft: `${offset}px` }}
      >
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
}

function MenuTrigger({
  label,
  sectionActive,
  widthClassName,
  className,
  renderBody,
}: {
  label: string;
  sectionActive: boolean;
  widthClassName: string;
  className?: string;
  renderBody: (closeMenu: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <li className={cn("relative", className)}>
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Trigger
          type="button"
          className={`flex items-center gap-1 rounded-full px-3 py-2 text-sm font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring${
            sectionActive || open
              ? " bg-brand-tint text-brand-strong"
              : " text-foreground hover:bg-muted"
          }`}
        >
          {label}
          <ChevronDown
            className={`size-3.5 transition-transform duration-200${open ? " rotate-180" : ""}`}
            aria-hidden
          />
        </PopoverPrimitive.Trigger>
        <MenuPanel open={open} widthClassName={widthClassName}>
          {renderBody(() => setOpen(false))}
        </MenuPanel>
      </PopoverPrimitive.Root>
    </li>
  );
}

function menuIsActive(menu: NavMenu, isActive: (href: string) => boolean) {
  return [menu.hub, ...menu.links].some((link) => link && isActive(link.href));
}

export function DesktopNavigation() {
  const isActive = useNavActive();

  return (
    <nav aria-label="Main" className="hidden md:block">
      <ul className="flex items-center gap-0.5">
        {NAV_MENUS.map((menu) => {
          const isResources = menu === RESOURCES_MENU;
          return (
            <MenuTrigger
              key={menu.label}
              label={menu.label}
              sectionActive={menuIsActive(menu, isActive)}
              widthClassName={isResources ? "w-56" : "w-[26rem]"}
              className={isResources ? "hidden xl:block" : undefined}
              renderBody={(closeMenu) => (
                <NavMenuLinks
                  menu={menu}
                  isActive={isActive}
                  onLinkSelect={closeMenu}
                  columns={isResources ? 1 : 2}
                />
              )}
            />
          );
        })}
      </ul>
    </nav>
  );
}
